import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'

import Container from '/src/components/@common.blocks/Container'
import AssetCard from '/src/components/@common.blocks/AssetCard'
import AssetRankCard from '/src/components/@common.blocks/AssetRankCard'
import AssetsFilter from '/src/components/@common.blocks/AssetsFilter'
import Modal from '/src/components/@common.blocks/Modal'
import { cutAddress, cutText } from '/src/helpers/formatter'
import { CoreApi } from '/src/helpers/CoreApi'
import { OpenseaApi } from '/src/helpers/OpenseaApi'
import useInfiniteScroll from '/src/hooks/useInfiniteScroll'
import useDebounce from '/src/hooks/useDebounce'
import Tabs, { TabPane } from '/src/components/@common.blocks/Tabs'
import Grid from '/src/components/@common.blocks/Grid'
import { setHistory } from '/src/helpers/history'
import Input from '/src/components/@common.blocks/Input'
import ActiveFilter from '/src/components/@common.blocks/ActiveFilter'
import Text from '/src/components/@common.blocks/Text'

const FETCH_SIZE = 10

const CollectionAssets = ({ collection }) => {
  const [query, setQuery] = useState({})
  const [assets, setAssets] = useState([])
  const [page, setPage] = useState(1)
  const [openIndex, setOpenIndex] = useState(-1)
  const [assetsOS, setAssetsOS] = useState({})
  const [loadingOS, setLoadingOS] = useState(false)

  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useInfiniteScroll(fetchData)
  const debouncedQuery = useDebounce(query, 300)

  const router = useRouter()

  const onFilterChange = (newQuery) => {
    const historyQuery = { ...newQuery }
    delete historyQuery.slug
    setHistory(historyQuery)
    setQuery({ ...newQuery })
  }

  useEffect(() => {
    setQuery(router.query)
  }, [])

  useEffect(() => {
    setLoaded(false)
    fetchData(true)
  }, [debouncedQuery])

  useEffect(() => {
    if (!assets) return
    fetchOpenSea()
  }, [assets])

  async function fetchOpenSea() {
    setLoadingOS(true)
    const tokenIds = assets
      .filter((el) => !assetsOS[el.tokenId])
      .map((el) => el.tokenId)

    if (tokenIds.length === 0) return

    const data = await OpenseaApi.getAssets(collection.contract, tokenIds)
    const newAssetsOS = { ...assetsOS }
    for (const itemOS of data) {
      newAssetsOS[itemOS.tokenId] = {
        price: itemOS.price,
        symbol: itemOS.symbol,
        owner: itemOS.owner || '',
        ownerName: itemOS.ownerName || '',
      }
    }
    setAssetsOS((prevState) => {
      return { ...prevState, ...newAssetsOS }
    })
    setLoadingOS(false)
  }

  async function fetchData(initialFetch) {
    if (loaded && !initialFetch) return
    const skeletonItems = []
    for (let i = 0; i < FETCH_SIZE; i++) {
      skeletonItems.push({ loading: true })
    }

    let data
    setAssets((prevState) => {
      data = initialFetch
        ? [...skeletonItems]
        : [...prevState, ...skeletonItems]
      return [...data]
    })

    const { data: assetsDB, total } = await CoreApi.getAssets({
      collection: collection.slug,
      tokenId: router.query.tokenId,
      page: initialFetch ? 1 : page + 1,
      size: FETCH_SIZE,
      ...debouncedQuery,
    })

    const result = assetsDB

    if (total <= result.length) {
      setLoaded(true)
    }
    return new Promise((resolve) => {
      setTimeout(() => {
        setAssets((prevState) => {
          const newData = [...data]
          if (result.length > 0) {
            for (const item of result) {
              const index = newData.findIndex((el) => el.loading)
              if (index === -1) break // TODO
              newData[index] = { ...item, loading: false }
            }
          }
          if (initialFetch) {
            setPage(1)
          } else {
            setPage(page + 1)
          }
          resolve(true)
          if (typeof router.query.tokenId !== 'undefined') {
            setOpenIndex(0)
          }
          return newData.filter((el) => !el.loading)
        })
      }, 300)
    })
  }

  const onCardClick = (e, index) => {
    if (assets[index]) {
      setOpenIndex(index)
    }
  }

  const modalOnClose = () => {
    setOpenIndex(-1)
  }

  const onFilterClear = (e, key) => {
    const newQuery = { ...query }
    delete newQuery[key]
    onFilterChange(newQuery)
  }

  const onFilterTraitClear = (e, key, value) => {
    const newQuery = { ...query }
    const traits = newQuery.traits ? JSON.parse(newQuery.traits) : {}
    if (traits && traits[key]) {
      traits[key] = traits[key].filter((el) => el !== value)
      if (traits[key].length === 0) {
        delete traits[key]
      }
      if (Object.keys(traits).length === 0) {
        delete newQuery.traits
      } else {
        newQuery.traits = JSON.stringify(traits)
      }
    }
    onFilterChange(newQuery)
  }

  const onFilterClearAll = () => {
    onFilterChange({})
  }

  const onQueryChange = (e, name, value) => {
    const newQuery = { ...query }

    if (name === 'rankMin' || name === 'rankMax') {
      const rankMin =
        name === 'rankMin'
          ? Number(value)
          : Number(query.rank?.split('-')[0]) || 1

      const rankMax =
        name === 'rankMax'
          ? Number(value)
          : Number(query.rank?.split('-')[1]) || collection.supply

      if (rankMin > 1 || rankMax < collection.supply) {
        newQuery.rank = `${rankMin}-${rankMax}`
      }
    } else if (name === 'sort') {
      newQuery[name] = value
      if (value === 'rank_asc') {
        delete newQuery[name]
      }
    } else {
      newQuery[name] = value
      if (!!value === false) {
        delete newQuery[name]
      }
    }

    onFilterChange(newQuery)
  }

  const filters = Object.keys(query)
    .reduce((items, key) => {
      let name = key
      let value = query[key]

      if (key.toLowerCase() === 'rank') {
        const rankMin = Number(value?.split('-')[0])
        const rankMax = Number(value?.split('-')[1])

        if (rankMin && rankMax && rankMax < collection.supply) {
          value = `${rankMin} — ${rankMax}`
        } else {
          value =
            '' +
            (rankMin > 0 ? `>= ${rankMin} ` : '') +
            (rankMax > 0 && rankMax < collection.supply ? `<= ${rankMax} ` : '')
        }
        items.push({
          id: key,
          name,
          value,
        })
      } else if (key.toLowerCase() === 'traits') {
        value = JSON.parse(value)
        for (const traitType of Object.keys(value)) {
          if (value[traitType] && value[traitType].length > 0) {
            items.push({
              id: traitType,
              name: traitType,
              value: [...value[traitType]],
              multiple: true,
            })
          }
        }
      } else {
        items.push({
          id: key,
          name,
          value,
        })
      }
      return items
    }, [])
    .filter(
      (item) =>
        item.id !== 'slug' && item.id !== 'tokenId' && item.id !== 'sort'
    )

  const activeFilters = filters.filter((el) => !el.multiple)
  const activeFiltersTraits = filters.filter((el) => el.multiple)

  const filterCount = activeFilters.length + activeFiltersTraits.length

  return (
    <section className='collection-assets'>
      <Container>
        <Tabs
          defaultActiveKey='1'
          onChange={(e) => console.log(e)}
          color='secondary'
        >
          <TabPane
            tab={
              <>
                NFTs <b>{`— ${collection.supply || 0}`}</b>
              </>
            }
            key='tabNFTs'
          >
            <Grid gutter={3}>
              <Grid.Col xs={12} sm={12} md={3}>
                <AssetsFilter
                  traits={collection.traits}
                  supply={collection.supply || 10000}
                  onChange={onFilterChange}
                  query={{ ...query }}
                  onQueryChange={onQueryChange}
                />
              </Grid.Col>
              <Grid.Col xs={12} sm={12} md={9}>
                <div className='collection-assets__filter-container'>
                  <ul className='collection-assets__active-filter'>
                    <li className='collection-assets__active-filter-search'>
                      <Input
                        name='tokenId'
                        placeholder='NFT id'
                        icon={<img src='/ui-icons/search.svg' alt='search' />}
                        value={query.tokenId}
                        onChange={(e) =>
                          onQueryChange(e, e.target.name, e.target.value)
                        }
                        onClear={(e) => onQueryChange(e, 'tokenId', undefined)}
                      />
                    </li>
                    {activeFilters.map((item, index) => (
                      <li key={index}>
                        <ActiveFilter
                          id={item.id}
                          name={item.name}
                          value={item.value}
                          onClear={onFilterClear}
                        />
                      </li>
                    ))}
                  </ul>
                  {activeFiltersTraits.length > 0 && (
                    <ul className='collection-assets__active-filter'>
                      {activeFiltersTraits.map((item, index) => (
                        <>
                          <li key={`${item.name}`}>
                            <div className='active-filter__header'>
                              <Text
                                color='secondary'
                                component='span'
                                weight='medium'
                                className='text-capitalize'
                              >
                                {`${item.name}:`}
                              </Text>
                            </div>
                          </li>
                          {item.value.map((value, index) => (
                            <li key={index}>
                              <ActiveFilter
                                id={item.name}
                                value={value}
                                onClear={onFilterTraitClear}
                              />
                            </li>
                          ))}
                        </>
                      ))}
                      {filterCount > 0 && (
                        <li>
                          <div
                            className='collection-assets__clear-filter'
                            onClick={onFilterClearAll}
                          >
                            Clear all
                          </div>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
                <Grid
                  gutter={{ xs: 2, lg: 3 }}
                  className='collection-assets__grid'
                >
                  {assets.map((item, index) => (
                    <Grid.Col
                      xs={6}
                      sm={4}
                      md={4}
                      xl={3}
                      key={`${item.name}_${index}`}
                    >
                      <AssetCard
                        loading={item.loading}
                        title={`#${item.tokenId}`}
                        rank={item.rank || 0}
                        image={item.image}
                        logo={collection.image}
                        owner={
                          assetsOS[item.tokenId]
                            ? assetsOS[item.tokenId].ownerName
                              ? cutText(assetsOS[item.tokenId].ownerName, 12)
                              : cutAddress(assetsOS[item.tokenId].owner)
                            : undefined
                        }
                        price={
                          assetsOS[item.tokenId]
                            ? assetsOS[item.tokenId].price
                            : undefined
                        }
                        footer={true}
                        onClick={(e) => onCardClick(e, index)}
                      />
                    </Grid.Col>
                  ))}
                </Grid>
              </Grid.Col>
            </Grid>
          </TabPane>
          <TabPane tab='Activity' key='tabActivity' disabled={true}>
            Coming soon
          </TabPane>
        </Tabs>
      </Container>
      <Modal open={openIndex >= 0} onClose={modalOnClose}>
        {openIndex >= 0 && (
          <AssetRankCard
            {...assets[openIndex]}
            rank={assets[openIndex].rank}
            totalScore={assets[openIndex].totalScore || 0}
            supply={collection.supply}
            logo={collection.image}
            {...assetsOS[assets[openIndex].tokenId]}
          />
        )}
      </Modal>
    </section>
  )
}

export default CollectionAssets
